import { useEffect, useState, type CSSProperties } from 'react';
import type { Customer } from '@setapart/shared';
import { FONTS, COLORS } from '@setapart/shared';
import { fetchSummary, startConnect, type CreatorSummary } from '../lib/api';

interface Props {
  customer: Customer;
  onDone: () => void;
  justReturned?: boolean;
}

export function ConnectStripe({ customer: _customer, onDone, justReturned }: Props) {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [summary, setSummary] = useState<CreatorSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await fetchSummary();
      if (res.ok) setSummary(res);
      else setError(res.error);
    })();
  }, []);

  async function onConnect() {
    if (!termsAccepted) {
      setError('Please accept the Creator Partner Terms first.');
      return;
    }
    if (!summary) return;
    setError(null);
    setLaunching(true);
    const res = await startConnect(summary.creator.id);
    setLaunching(false);
    if (res.ok && res.url) {
      window.location.href = res.url;
    } else {
      setError(res.error ?? 'Could not start onboarding');
    }
  }

  if (error === 'not_a_creator') {
    return <p style={{ color: COLORS.accent }}>You're not yet provisioned as a creator.</p>;
  }
  if (!summary) return <div style={{ color: COLORS.muted }}>Loading…</div>;

  const connected = summary.stripe.chargesEnabled;

  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.4em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.accent, marginBottom: 8 }}>
        PAYOUTS
      </div>
      <h1 style={{ fontFamily: FONTS.display, fontSize: 28, color: COLORS.ink, margin: 0, fontStyle: 'italic', fontWeight: 500 }}>
        Connect Stripe
      </h1>
      <p style={{ fontSize: 14, color: COLORS.muted, marginTop: 10, maxWidth: 560, lineHeight: 1.5 }}>
        Set Apart pays your commission directly through Stripe on the 1st of
        each month. Takes about 5 minutes to set up — you'll need a bank
        account or debit card for deposits.
      </p>

      {justReturned && (
        <div style={returnBanner}>
          Back from Stripe. Your account state is refreshing — this card updates within a few seconds.
        </div>
      )}

      {summary.stripe.accountId && (
        <div style={statusBox}>
          <div style={statusRow}>
            <span style={statusLabel}>Account</span>
            <code style={{ fontSize: 11 }}>{summary.stripe.accountId}</code>
          </div>
          <div style={statusRow}>
            <span style={statusLabel}>Status</span>
            <span style={{ color: connected ? '#1C3A2E' : COLORS.accent, fontWeight: 700, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {summary.stripe.status}
            </span>
          </div>
          <div style={statusRow}>
            <span style={statusLabel}>Can receive payouts</span>
            <span>{summary.stripe.payoutsEnabled ? '✓' : '—'}</span>
          </div>
        </div>
      )}

      <section style={termsBox} data-testid="creator-terms">
        <div style={{ fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.muted, marginBottom: 10 }}>
          CREATOR PARTNER TERMS
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: COLORS.ink }}>
          <li>I receive a share ({Math.round(summary.creator.commissionRate * 100)}%) of Set Apart's platform fee on sales attributed to my code.</li>
          <li>Attribution window is 30 days from first click. If another creator sets attribution after me within that window, theirs wins.</li>
          <li>Stripe pays me directly. Set Apart never holds my earnings.</li>
          <li>If a customer refunds a purchase attributed to me within 30 days, my share of that sale is clawed back from my next payout.</li>
          <li>I will not use fraudulent clicks, paid traffic I don't disclose, or incentivized installs to inflate attribution.</li>
          <li>I warrant that all content I share linking to Set Apart is my own or licensed for use.</li>
          <li>Either party may terminate this arrangement anytime. Attributed orders in-flight complete normally.</li>
          <li>Tax: Set Apart reports YTD earnings; I'm responsible for my own tax filings. Stripe issues a 1099-K if I cross $20K / 200 transactions.</li>
        </ul>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, cursor: 'pointer' }}>
          <input
            data-testid="creator-terms-checkbox"
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span style={{ fontSize: 13, color: COLORS.ink }}>
            I agree to the Creator Partner Terms.
          </span>
        </label>
      </section>

      {error && <div style={{ color: COLORS.accent, fontSize: 12, marginTop: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button
          data-testid="creator-connect-stripe-start"
          onClick={onConnect}
          disabled={!termsAccepted || launching}
          style={{
            ...primaryBtn,
            opacity: !termsAccepted || launching ? 0.5 : 1,
            cursor: !termsAccepted || launching ? 'default' : 'pointer',
          }}
        >
          {launching ? 'Launching Stripe…' : connected ? 'Relaunch Stripe dashboard' : 'Continue to Stripe →'}
        </button>
        <button onClick={onDone} style={secondaryBtn}>← Back</button>
      </div>
    </div>
  );
}

const statusBox: CSSProperties = {
  marginTop: 18,
  padding: 16,
  background: '#fff',
  border: `1px solid ${COLORS.sand}`,
  borderRadius: 14,
  maxWidth: 560,
};
const statusRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '6px 0',
  fontSize: 13,
  color: COLORS.ink,
};
const statusLabel: CSSProperties = {
  color: COLORS.muted,
  fontSize: 11,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  fontWeight: 700,
};
const termsBox: CSSProperties = {
  marginTop: 20,
  padding: 18,
  background: '#fff',
  border: `1px solid ${COLORS.sand}`,
  borderRadius: 14,
  maxWidth: 720,
};
const primaryBtn: CSSProperties = {
  padding: '12px 20px',
  borderRadius: 999,
  background: COLORS.ink,
  color: COLORS.cream,
  border: 'none',
  fontSize: 11,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  fontWeight: 700,
};
const secondaryBtn: CSSProperties = {
  padding: '12px 20px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 11,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: COLORS.muted,
};
const returnBanner: CSSProperties = {
  marginTop: 14,
  padding: 12,
  background: 'rgba(28,58,46,0.08)',
  border: `1px solid rgba(28,58,46,0.3)`,
  borderRadius: 12,
  fontSize: 13,
  color: COLORS.ink,
  maxWidth: 560,
};
